import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import type { Modifier } from "@popperjs/core";
import React from "react";
import { getElementZoom } from "../helpers/screenScale";
import "./PiPopper.css";

interface PiPopperProps {
  open: boolean;
  anchorRef: React.RefObject<HTMLDivElement>;
  onClose: (event: Event | undefined) => void;
  items: PiPopperMenuProps[];
  selectedIndex: number;
  width: string;
  onClick?: (index: number) => void;
  disabled?: boolean;
  placement?: "bottom-start" | "bottom" | "top-start" | "top";
  disableFlip?: boolean;
  menuClassName?: string;
  paperClassName?: string;
}

export interface PiPopperMenuProps {
  text: string;
  img?: string;
  onClick?: (index: number) => void;
}

export default function PiPopper(props: PiPopperProps) {
  const { open, anchorRef, onClose, items, width, placement, disableFlip } = {
    ...props,
  };
  const modifiers: Array<
    Partial<Modifier<string, Record<string, never>>>
  > = [];

  // Screen scaling puts a CSS zoom on #root. Popper.js cannot position an element
  // that lives inside a zoomed ancestor reliably, so a menu opened from the app
  // renders in <body> (unzoomed, where positioning is exact) and zooms only its
  // content to match the anchor. Inside a dialog (already portalled, unzoomed) it
  // stays in place, so the dialog's focus trap keeps working.
  const anchor = anchorRef.current;
  const inZoomedApp = !!anchor?.closest("#root");
  const contentZoom = inZoomedApp && anchor ? getElementZoom(anchor) : 1;
  const zoomed = contentZoom !== 1;

  if (width === "100%") {
    modifiers.push({
      name: "matchAnchorWidth",
      enabled: true,
      phase: "beforeWrite",
      requires: ["computeStyles"],
      fn: ({ state }) => {
        // the menu is never inside a zoom, so on-screen px are CSS px here
        state.styles.popper.width = `${state.rects.reference.width}px`;
      },
    });
  }

  if (disableFlip) {
    modifiers.push({ name: "flip", enabled: false });
  }

  return (
    <Popper
      sx={{
        zIndex: 1002,
        width:
          width === "100%"
            ? undefined
            : zoomed
              ? `calc(${width} * ${contentZoom})`
              : width,
      }}
      open={open}
      anchorEl={anchorRef.current}
      // A full-width menu aligns to the anchor's left edge. Centring ("bottom")
      // uses the menu's width from before matchAnchorWidth applies, which shifts it.
      placement={placement ?? (width === "100%" ? "bottom-start" : undefined)}
      modifiers={modifiers}
      role={undefined}
      transition
      disablePortal={!inZoomedApp}
      aria-label="pisplitbutton-popper"
    >
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin: placement.startsWith("bottom") ? "center top" : "center bottom",
            transform: "translate(0, 8px)",
            borderRadius: "10px",
          }}
        >
          <Paper
            className={props.paperClassName}
            style={zoomed ? { zoom: contentZoom } : undefined}
          >
            <ClickAwayListener onClickAway={onClose}>
              <MenuList
                className={
                  props.menuClassName
                    ? `pipopper-menu ${props.menuClassName}`
                    : "pipopper-menu"
                }
                id="split-button-menu"
                autoFocusItem
                style={{
                  maxHeight: "250px",
                  overflowY: "auto",
                }}
              >
                {items.map((option, index) => (
                  <MenuItem
                    aria-label={option.text}
                    key={option.text}
                    selected={index === props.selectedIndex}
                    onClick={(event) => {
                      option.onClick?.(index);
                      props.onClose(undefined);
                      props.onClick?.(index);
                    }}
                    className="pipopper-menuitem"
                  >
                    {option.text}
                  </MenuItem>
                ))}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
}
